import React from 'react'
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Editor } from '@tiptap/core'
import { cn } from '@/lib/utils'
import { CitationAttributes } from './citation-extension'
import {
  validateCitationAttrs,
  getCitationWarning,
  isCitationClickable,
} from './citation-validator'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@deepguide-ai/ui/hover-card'
import { ExternalLink } from 'lucide-react'
import { useAtom } from 'jotai'
import { workspaceIdAtom, activeWorkspaceWithStorageAtom } from '@/stores/workspaceStore'
import { ensureInternalDocParams } from '@/utils/link-helpers'

interface CitationNodeViewProps {
  node: ProseMirrorNode
  editor: Editor
  getPos: () => number | undefined
  updateAttributes: (attributes: Record<string, any>) => void
  deleteNode: () => void
  selected: boolean
}

export function CitationNodeView({
  node,
  editor,
  getPos,
  updateAttributes,
  deleteNode,
  selected,
}: CitationNodeViewProps) {
  const attrs = node.attrs as CitationAttributes
  const [workspaceId] = useAtom(workspaceIdAtom)
  const [workspace] = useAtom(activeWorkspaceWithStorageAtom)

  const { id, title, slug, topicId, docId, sourceType, updatedAt, isBrokenLink } = attrs

  // Debug: Log citation attributes to check if topicId is present
  if (process.env.NODE_ENV === 'development') {
    console.log('Citation attrs:', { id, title, slug, topicId, docId, sourceType })
  }

  // Citations are always internal documents
  const isInternal = true // sourceType should always be 'Doc'
  
  // Build internal link with workspace slug, and append topicId/docId if provided
  const buildInternalHref = (withDocId?: string): string | undefined => {
    if (!slug) return undefined
    const fromPath = typeof window !== 'undefined' ? (window.location.pathname.match(/^\/workspaces\/([^/]+)/)?.[1] || '') : ''
    const workspaceSlug = localStorage.getItem('auth:activeWorkspaceSlug') || fromPath || ''
    const base = workspaceSlug ? `/workspaces/${workspaceSlug}/docs/${slug}` : `/docs/${slug}`
    const params = new URLSearchParams()
    
    // Always add topicId if it exists
    if (topicId) {
      params.set('topicId', withDocId, docId)
    }
    console.log('topicId', topicId)
    // Add docId if provided
    const dId = withDocId || docId
    if (dId) {
      params.set('docId', dId)
    }
    
    const qs = params.toString()
    const finalUrl = qs ? `${base}?${qs}` : base
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Built Citation URL:', finalUrl, { topicId, docId: dId })
    }
    
    return finalUrl
  }
  
  const href = buildInternalHref()
  
  // No external icon for citations (external links should use Link mark instead)
  const Icon = null

  const formattedDate = updatedAt
    ? new Date(updatedAt).toLocaleDateString()
    : '—'

  const handleClick = async (e: React.MouseEvent) => {
    if (isBrokenLink || !href) return
    e.preventDefault()
    e.stopPropagation()

    // Save current document before navigation
    try {
      const saver = (editor as any)?.forceSaveDocument || (window as any)?.tiptapForceSaveDocument
      if (typeof saver === 'function') {
        await saver()
      }
    } catch (_e) {
      // Continue navigation if saving fails.
    }

    if (isInternal) {
      // Ensure href has complete topicId and docId parameters
      const finalHref = workspaceId
        ? await ensureInternalDocParams(href, workspaceId, undefined, workspace?.slug)
        : href

      if (process.env.NODE_ENV === 'development') {
        console.log('[Citation] Navigate to:', finalHref, {
          originalHref: href,
          slug,
          topicId,
          docId
        })
      }

      window.location.href = finalHref
    } else {
      window.open(href, '_blank', 'noopener,noreferrer')
    }
  }

  // Validate citation data using validator
  const isValid = validateCitationAttrs(attrs)
  const warning = getCitationWarning(attrs)
  const isClickable = isCitationClickable(attrs)

  return (
    <NodeViewWrapper
      as="span"
      className={cn(
        'inline',
        selected && 'ProseMirror-selectednode'
      )}
    >
      <HoverCard>
        <HoverCardTrigger asChild>
          <span
            onClick={isClickable ? handleClick : undefined}
            className={cn(
              'citation-link inline-flex items-center',
              isClickable 
                ? 'cursor-pointer text-blue-500 underline decoration-blue-500 underline-offset-2 hover:decoration-2'
                : 'text-gray-500',
              isBrokenLink && 'text-destructive opacity-60',
              warning && !isBrokenLink && 'border-b border-dashed border-amber-400'
            )}
            title={title || warning || undefined}
            data-citation-id={id}
            data-source-type={sourceType}
            data-slug={slug}
            data-doc-id={docId}
            data-topic-id={topicId}
          >
            <NodeViewContent />
            {Icon && (
              <Icon className='ml-1 h-3 w-3 inline-block' />
            )}
          </span>
        </HoverCardTrigger>
        <HoverCardContent className='w-80 p-3'>
          <div className='space-y-2'>
            <div className='font-semibold text-sm'>
              {title || 'Citation'}
            </div>
            <div className='text-xs text-muted-foreground'>
              {sourceType || 'Unknown Source'}
            </div>
            
            {/* Warning messages */}
            {warning && (
              <div className={cn(
                'text-xs',
                isBrokenLink ? 'text-destructive' : 'text-status-orange'
              )}>
                ⚠️ {warning}
              </div>
            )}
            
            {/* Link action for non-editable mode */}
            {!editor.isEditable && href && isClickable && (
              <div className='pt-2 text-xs'>
                <a
                  href={href}
                  target={isInternal ? '_self' : '_blank'}
                  rel='noopener noreferrer'
                  className='text-cobalt-strong underline'
                  onClick={(e) => {
                    e.stopPropagation()
                    handleClick(e)
                  }}
                >
                  Open {isInternal ? 'document' : 'link'} ↗
                </a>
              </div>
            )}
            
            {/* Metadata */}
            <div className='flex items-center justify-between text-xs text-muted-foreground pt-2 border-t'>
              <div className='flex items-center gap-1'>
                {Icon && <Icon className='h-3 w-3' />}
                <span>{sourceType}</span>
              </div>
              {formattedDate && (
                <div>{formattedDate}</div>
              )}
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    </NodeViewWrapper>
  )
}
